/**
 * @file PGSTaskDetail.js
 * @description Post Grant Support — Details View (COL-139).
 *
 * Mirrors the OA Shell detail page (OAWorkflowTaskDetail) so both workflows
 * behave and look the same: Processor/QA assignment panel, workflow Submit
 * with confirmation, and package attach/download — plus the PGS-specific
 * field set, with Page No., Complexity, Deadline and Error/No Error editable
 * by Admin/AM.
 *
 * Route: /collaboration-space/pgs-workflow/tasks/:taskId
 */

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Typography, CircularProgress, Alert, Paper, Divider, IconButton, Tooltip, Backdrop,
    Chip, FormControl, Select, MenuItem, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltIcon from "@mui/icons-material/Bolt";
import { axios_tenant } from "../../../../axios";
import useTaskDetail from "../../../shared/OAWorkflow/hooks/useTaskDetail";
import TaskDetailPanel from "../../../shared/OAWorkflow/components/TaskDetailPanel";
import PackageSection from "../../../shared/OAWorkflow/components/PackageSection";
import Button from "../../../shared/OAWorkflow/components/atoms/Button";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import {
    isSubmitEnabled as isSubmitEnabledShared,
    getSubmitDisabledReason as getSubmitDisabledReasonShared,
    getSubmitConfirmMessage as getSubmitConfirmMessageShared,
    hasRequiredPackages
} from "../../../shared/OAWorkflow/utils/taskTransitions";
import { formatDateDDMMMYYYY, formatOADateTime } from "../../../shared/OAWorkflow/utils/dateUtils";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

/** Plain read-only value node, styled to match TaskDetailPanel's other fields. */
const ReadOnly = ({ children }) => (
    <Typography variant="body2" sx={{ color: '#111827' }}>{children ?? "—"}</Typography>
);

/**
 * COL-139: billable time is persisted as whole minutes (summable/sortable) and
 * presented as HH:MM.
 */
const formatMinutesAsHHMM = (minutes) => {
    if (minutes === null || minutes === undefined || isNaN(minutes)) return null;
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};

const PGSTaskDetail = () => {
    useDocumentTitle("Post Grant Support — Task Details");
    const { taskId } = useParams();
    const navigate = useNavigate();
    const outerTheme = useTheme();
    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: { fontFamily: 'Lato, sans-serif' }
    }), [outerTheme]);

    const {
        task, packages, permittedTransitions, submitTransitions,
        allowedActions, canAdminAssign, loading, error, refetch
    } = useTaskDetail(taskId);

    const [workflowUsers, setWorkflowUsers] = useState([]);
    const [userRole, setUserRole] = useState("Client");
    const [localProcessorId, setLocalProcessorId] = useState(null);
    const [localQaId, setLocalQaId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));
    const showSuccess = (message) => setSnackbar({ open: true, message, severity: "success" });
    const showError = (message) => setSnackbar({ open: true, message, severity: "error" });

    const isAdminOrAM = userRole === "Admin" || userRole === "Application Manager";
    const isInternalRole = ["Admin", "Application Manager", "Processor", "QA"].includes(userRole);

    // Sync staged assignment state from the task on load/refetch
    React.useEffect(() => {
        if (task) {
            setLocalProcessorId(task.processorId || null);
            setLocalQaId(task.qcRequired === false ? "NOT_REQUIRED" : (task.qaId || null));
        }
    }, [task?.processorId, task?.qaId, task?.qcRequired]);

    React.useEffect(() => {
        const fetchMeta = async () => {
            try {
                const meRes = await axios_tenant.get(`${BASE_URL}/me?workType=Post Grant Support`);
                setUserRole(meRes.data.role);
                if (meRes.data.role === "Admin" || meRes.data.role === "Application Manager") {
                    const uRes = await axios_tenant.get(`${BASE_URL}/oashell-users`, {
                        params: { workType: "Post Grant Support" }
                    });
                    setWorkflowUsers((uRes.data.data || []).map(u => ({
                        id: u.id,
                        email: u.email,
                        displayName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                        role: { roleName: u.roleName, canActAs: u.canActAs || [] },
                        roleId: u.roleId
                    })));
                }
            } catch (err) {
                console.error("PGS detail meta fetch error:", err);
            }
        };
        fetchMeta();
    }, [taskId]);

    // The task stores the PGS client code (e.g. "wbd"); show the directory name
    // the PGS list shows. Any failure falls back to the code.
    const [pgsClients, setPgsClients] = useState([]);
    React.useEffect(() => {
        if (!isInternalRole) return;
        let cancelled = false;
        axios_tenant.get(`${BASE_URL}/intake/clients`, { params: { workType: "Post Grant Support" }, silent: true })
            .then(res => { if (!cancelled) setPgsClients(res.data.clients || []); })
            .catch(() => { if (!cancelled) setPgsClients([]); });
        return () => { cancelled = true; };
    }, [isInternalRole]);
    const clientCode = task?.clientName || task?.routedClient;
    const clientDisplayName = pgsClients.find(c => c.tenantCode === clientCode)?.tenantName || null;

    // ── Field edits (Admin/AM) ──
    const [savingField, setSavingField] = useState(false);
    const saveField = async (payload, successMsg) => {
        try {
            setSavingField(true);
            await axios_tenant.put(`${BASE_URL}/tasks/${taskId}/admin-edit`, payload, { silent: true });
            showSuccess(successMsg);
            refetch();
        } catch (err) {
            showError(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingField(false);
        }
    };

    // ── Error / No Error edit (COL-142, Processor/QA/AM/Admin — own endpoint) ──
    const saveErrorStatus = async (errorNoError) => {
        try {
            setSavingField(true);
            await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/error-status`, { errorNoError }, { silent: true });
            showSuccess(`Error status set to ${errorNoError}`);
            refetch();
        } catch (err) {
            showError(err?.response?.data?.message || "Failed to update Error / No Error");
        } finally {
            setSavingField(false);
        }
    };

    // ── Assignment (non-admin path writes immediately) ──
    const [isAssigning, setIsAssigning] = useState(false);
    const handleAssign = async ({ processorId, qaId }) => {
        try {
            setIsAssigning(true);
            await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, { processorId, qaId }, { silent: true });
            refetch();
        } catch (err) {
            showError(err?.response?.data?.message || "Assignment failed");
        } finally {
            setIsAssigning(false);
        }
    };

    // ── Workflow transitions ──
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    const handleStatusChange = async (toStatus) => {
        try {
            setIsSubmitting(true);
            await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus }, { silent: true });
            showSuccess(`Status changed to ${toStatus}`);
            refetch();
        } catch (err) {
            showError(err?.response?.data?.message || "Status change failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    const displayQA = (() => {
        if (localQaId === "NOT_REQUIRED") return "Not Required";
        const qaId = localQaId || (task && task.qaId);
        if (!qaId) return "Unassigned";
        const qa = workflowUsers.find(u => u.id === qaId);
        return qa ? qa.displayName : (task?.qaName || `ID: ${qaId}`);
    })();

    const handleConfirmedAction = async () => {
        try {
            setIsSubmitting(true);
            setConfirmOpen(false);

            if (confirmAction === 'assign') {
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, {
                    processorId: localProcessorId,
                    qaId: localQaId || undefined
                }, { silent: true });
                showSuccess("Task assigned successfully");
            } else if (confirmAction === 'assignQa') {
                if (localQaId && localQaId !== task.qaId) {
                    await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, { qaId: localQaId }, { silent: true });
                }
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'QC Pending' }, { silent: true });
                showSuccess(`Task assigned to ${displayQA} and moved to QC Pending`);
            } else if (confirmAction === 'qcPending') {
                // silent: true — this page already shows its own error via
                // showError() below; without it, the global axios interceptor's
                // toast fires too, overlapping with this page's Snackbar.
                const res = await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'QC Pending' }, { silent: true });
                const actualStatus = res.data?.data?.taskStatus;
                showSuccess(actualStatus === 'Assign QA' || !task.qaId
                    ? 'Task submitted — moved to "Assign QA" as no QA is assigned'
                    : "Task submitted for QC review");
            } else if (confirmAction === 'completeNoQc') {
                // COL-137: QA marked "Not Required" — processing completes the task.
                // silent: true — same duplicate-toast fix as the qcPending branch above.
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'Completed' }, { silent: true });
                showSuccess("Task completed (QC not required)");
            } else if (confirmAction === 'qaSubmit') {
                await axios_tenant.post(`${BASE_URL}/tasks/${taskId}/qa-submit`, undefined, { silent: true });
                showSuccess("Task completed successfully");
            }
            refetch();
        } catch (err) {
            showError(err?.response?.data?.message || "Action failed. Please try again.");
        } finally {
            setIsSubmitting(false);
            setConfirmAction(null);
        }
    };

    const handlePrimarySubmit = () => {
        if (canAdminAssign && task.taskStatus === 'Assign QA') {
            setConfirmAction('assignQa');
        } else if (canAdminAssign) {
            setConfirmAction('assign');
        } else if (submitTransitions.includes('Completed') && task.qcRequired === false) {
            setConfirmAction('completeNoQc');
        } else if (submitTransitions.includes('QC Pending')) {
            setConfirmAction('qcPending');
        } else if (submitTransitions.includes('Completed')) {
            setConfirmAction('qaSubmit');
        } else {
            return;
        }
        setConfirmOpen(true);
    };

    // COL-158: upload-optional clients satisfy the Processor gate without a file
    const hasPackages = hasRequiredPackages(task, packages && packages.length > 0);
    const isSubmitEnabled = isSubmitEnabledShared(task, hasPackages, userRole, localProcessorId, localQaId);
    const submitDisabledReason = getSubmitDisabledReasonShared(task, hasPackages, userRole, localProcessorId, localQaId);

    const getConfirmMessage = () => {
        if (!task) return "";
        let submitTarget = null;
        if (confirmAction === 'assign') submitTarget = "In Progress";
        else if (confirmAction === 'assignQa') submitTarget = "QC Pending";
        else if (confirmAction === 'qcPending') submitTarget = task.qaId ? "QC Pending" : "Assign QA";
        else if (confirmAction === 'completeNoQc') return "QA is marked Not Required for this task. Submitting will mark it Completed without QC review. Continue?";
        else if (confirmAction === 'qaSubmit') submitTarget = "Completed";
        return getSubmitConfirmMessageShared(submitTarget, task, localQaId);
    };

    // ── Packages (PGS upload endpoint) ──
    const [isUploading, setIsUploading] = useState(false);
    const [downloadingVersion, setDownloadingVersion] = useState(null);

    const handlePackageUpload = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".zip,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg";
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("file", file);
            try {
                setIsUploading(true);
                await axios_tenant.post(`${BASE_URL}/tasks/${taskId}/pgs-package`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    silent: true
                });
                showSuccess("File uploaded successfully");
                refetch();
            } catch (err) {
                showError(err?.response?.data?.message || "Upload failed");
            } finally {
                setIsUploading(false);
            }
        };
        input.click();
    };

    const handlePackageDownload = async (version) => {
        try {
            setDownloadingVersion(version);
            const res = await axios_tenant.get(`${BASE_URL}/tasks/${taskId}/package/${version}`, { responseType: "blob", silent: true });
            const pkg = (packages || []).find(p => p.version === version);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = pkg?.fileName || `${taskId}_v${version}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showError("Download failed");
        } finally {
            setDownloadingVersion(null);
        }
    };

    const canUpload = allowedActions.includes('upload');
    const isPageBusy = isUploading || isSubmitting || isAssigning || savingField;

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND_COLORS.PRIMARY }} /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
    if (!task) return <Alert severity="warning" sx={{ m: 3 }}>Task not found.</Alert>;

    const ex = task.extraFields || {};
    const isCompleted = task.taskStatus === "Completed";

    // Mirrors TaskTable's deadline flag: "Due Today" when the deadline is
    // reached, "Overdue" once passed. Completed tasks are never flagged.
    const deadlineFlag = (() => {
        if (!task.deadlineDate || isCompleted) return null;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const dl = new Date(`${String(task.deadlineDate).slice(0, 10)}T00:00:00`);
        if (isNaN(dl.getTime()) || dl > today) return null;
        return dl < today ? "Overdue" : "Due Today";
    })();

    // COL-139/COL-142: PGS-specific fields merged into TaskDetailPanel's top
    // section (renamed "Task Details" below) alongside the OA fields it
    // already renders (Application No., Client Name, Docket No., Activity).
    const pgsDetailFields = [
        { key: "patentNo", label: "Patent No.", node: <ReadOnly>{ex.patentNo}</ReadOnly> },
        {
            key: "pageNo", label: "Page No.",
            node: isAdminOrAM ? (
                <TextField
                    type="number" size="small" defaultValue={ex.pageNo ?? ""}
                    inputProps={{ min: 0 }}
                    onBlur={(e) => {
                        let v = e.target.value;
                        if (v !== "" && Number(v) < 0) { v = "0"; e.target.value = v; }
                        if (String(v) !== String(ex.pageNo ?? "")) saveField({ pageNo: v === "" ? null : Number(v) }, "Page No. updated");
                    }}
                    sx={{ width: 110 }}
                />
            ) : <ReadOnly>{ex.pageNo}</ReadOnly>
        },
        {
            key: "complexity", label: "Complexity",
            node: (isAdminOrAM && !isCompleted) ? (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                        value={task.complexity || ""}
                        displayEmpty
                        onChange={(e) => saveField({ complexity: e.target.value }, `Complexity set to ${e.target.value}`)}
                    >
                        <MenuItem value="" disabled>Select</MenuItem>
                        <MenuItem value="Simple">Simple</MenuItem>
                        <MenuItem value="Complex">Complex</MenuItem>
                    </Select>
                </FormControl>
            ) : <ReadOnly>{task.complexity}</ReadOnly>
        },
        { key: "incomingDate", label: "Incoming Date", node: <ReadOnly>{formatDateDDMMMYYYY(task.requestedDate)}</ReadOnly> },
        {
            key: "deadlineDate", label: "Deadline Date",
            // The Overdue / Due Today flag shows for every role — editors get it
            // beneath the date picker, viewers beneath the plain value.
            node: (
                <Box>
                    {isAdminOrAM ? (
                        <TextField
                            // Uncontrolled input: re-key on the value so a refetch
                            // (e.g. after toggling Rush) shows the new deadline.
                            key={`deadline-${task.deadlineDate || ""}`}
                            type="date" size="small"
                            defaultValue={task.deadlineDate ? String(task.deadlineDate).slice(0, 10) : ""}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: String(task.requestedDate || "").slice(0, 10) || undefined }}
                            onBlur={(e) => {
                                const v = e.target.value;
                                const current = String(task.deadlineDate || "").slice(0, 10);
                                if (!v || v === current) return;
                                const incomingDate = String(task.requestedDate || "").slice(0, 10);
                                if (incomingDate && v < incomingDate) {
                                    showError("Deadline Date cannot be earlier than the Incoming Date");
                                    e.target.value = current;
                                    return;
                                }
                                saveField({ deadlineDate: v }, "Deadline updated");
                            }}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ color: deadlineFlag ? "#DC2626" : "#111827", fontWeight: deadlineFlag ? 700 : 400 }}>
                            {formatDateDDMMMYYYY(task.deadlineDate) || "—"}
                        </Typography>
                    )}
                    {deadlineFlag && (
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#DC2626", mt: 0.5 }}>{deadlineFlag}</Typography>
                    )}
                </Box>
            )
        },
        // created_by is populated at creation (and backfilled from the
        // TASK_CREATED audit); no updatedBy fallback — that's the last
        // modifier, not the creator.
        { key: "createdBy", label: "Created By", node: <ReadOnly>{task.createdBy ? task.createdBy.replace(/\s*\([^)]+\)$/, "") : "—"}</ReadOnly> },
        { key: "sourceFile", label: "Source File", node: <ReadOnly>{task.sourceFile}</ReadOnly> },
        {
            key: "errorNoError", label: "Error / No Error",
            node: (isInternalRole && !isCompleted) ? (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                        value={task.errorNoError || ""}
                        displayEmpty
                        onChange={(e) => saveErrorStatus(e.target.value)}
                    >
                        <MenuItem value="" disabled>Select</MenuItem>
                        <MenuItem value="Error">Error</MenuItem>
                        <MenuItem value="No Error">No Error</MenuItem>
                    </Select>
                </FormControl>
            ) : <ReadOnly>{task.errorNoError}</ReadOnly>
        },
        { key: "processingBt", label: "Processing BT (HH:MM)", node: <ReadOnly>{formatMinutesAsHHMM(task.processingBtMinutes)}</ReadOnly> },
        { key: "processingDate", label: "Processing Date", node: <ReadOnly>{formatOADateTime(task.processingDate)}</ReadOnly> },
        { key: "qcBt", label: "QC BT (HH:MM)", node: <ReadOnly>{formatMinutesAsHHMM(task.qcBtMinutes)}</ReadOnly> },
        { key: "qcDate", label: "QC Date", node: <ReadOnly>{formatOADateTime(task.qcDate)}</ReadOnly> }
    ];

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ pt: 1, px: 4, pb: 10, position: 'relative' }} data-testid="pgs-task-detail-page">
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                    <Tooltip title="Back">
                        <IconButton onClick={() => navigate(-1)} size="small"><ArrowBackIcon /></IconButton>
                    </Tooltip>
                    <Typography variant="h5" fontWeight={600} sx={{ color: '#1a1a2e', fontSize: '20px' }}>
                        Details
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#64748B" }}>{task.taskId}</Typography>
                    {task.isRush && (
                        <Chip
                            icon={<BoltIcon sx={{ fontSize: "14px !important", color: "#DC2626 !important" }} />}
                            label="RUSH"
                            size="small"
                            sx={{ height: 22, fontWeight: 700, fontSize: "11px", color: "#DC2626", backgroundColor: "#FEE2E2" }}
                        />
                    )}
                </Box>

                <Paper sx={{ borderRadius: 2, backgroundColor: '#fff', mb: 3 }}>
                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Assignment + status + Task Details (merged, COL-139) */}
                        <TaskDetailPanel
                            task={task}
                            users={workflowUsers}
                            onAssign={canAdminAssign ? null : handleAssign}
                            onLocalAssign={canAdminAssign ? { setProcessorId: setLocalProcessorId, setQaId: setLocalQaId, processorId: localProcessorId, qaId: localQaId } : null}
                            onStatusChange={handleStatusChange}
                            permittedTransitions={permittedTransitions}
                            currentRole={userRole}
                            isAssigning={isAssigning}
                            sectionTitle="Task Details"
                            hiddenFields={["clientType", "applicationStatus", "oaMailDate", "attorneyInitials", "clientFileNumber"]}
                            additionalFields={pgsDetailFields}
                            taskIdLabel="Task ID"
                            clientDisplayName={clientDisplayName}
                        />

                        {/* COL-147: same separator TaskDetailPanel uses between its
                            sections, so Attachment reads as a section of its own.
                            Colour matches the table row borders (PackageSection). */}
                        <Divider sx={{ borderColor: '#CBD5E1', borderBottomWidth: 2, my: 0.5 }} />

                        {/* Attachments — heading and Upload button share one row (COL-147) */}
                        <Box>
                            <PackageSection
                                title="Attachment"
                                packages={packages}
                                canUpload={canUpload}
                                onUpload={handlePackageUpload}
                                onDownload={handlePackageDownload}
                                downloadingVersion={downloadingVersion}
                                isUploading={isUploading}
                                users={workflowUsers}
                            />
                        </Box>
                    </Box>

                    {/* Submit footer — hidden once Completed */}
                    {task.taskStatus !== "Completed" && (
                        <Box sx={{ p: 3, borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            {submitDisabledReason && (
                                <Typography variant="caption" sx={{ color: '#EF4444', fontStyle: 'italic' }}>
                                    {submitDisabledReason}
                                </Typography>
                            )}
                            <Button
                                variant="primary"
                                onClick={handlePrimarySubmit}
                                disabled={!isSubmitEnabled || isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : "Submit"}
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* Confirmation modal */}
                <Dialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, minWidth: 400, p: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#1a1a2e', pb: 1 }}>
                        Confirm Submission
                    </DialogTitle>
                    <DialogContent sx={{ pb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6 }}>
                            {getConfirmMessage()}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>No</Button>
                        <Button variant="primary" onClick={handleConfirmedAction} disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : "Yes"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <Backdrop
                    open={isPageBusy}
                    sx={{
                        position: 'absolute',
                        zIndex: (theme) => theme.zIndex.modal - 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: 2
                    }}
                >
                    <CircularProgress size={40} sx={{ color: BRAND_COLORS.PRIMARY }} />
                </Backdrop>
            </Box>
        </ThemeProvider>
    );
};

export default PGSTaskDetail;
